import { useState } from "react";
import { ArrowRight, CheckCircle, X, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API } from "../App";

const KEY = "pwm_nl_hidden_until";
const THIRTY_DAYS = 30 * 24 * 3600 * 1000;

export const isNewsletterHidden = () => {
  try { return Number(localStorage.getItem(KEY) || 0) > Date.now(); } catch { return false; }
};
const hideFor30Days = () => { try { localStorage.setItem(KEY, String(Date.now() + THIRTY_DAYS)); } catch { /* storage disabled */ } };

// Compact subscribe card. variant: "inline" (inside the article body) | "sidebar" (right rail).
export const InlineSubscribe = ({ variant = "inline" }) => {
  const [hidden, setHidden] = useState(isNewsletterHidden);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (hidden) return null;

  const dismiss = () => { hideFor30Days(); setHidden(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, source: `article-${variant}` });
      setDone(true);
      hideFor30Days();
      toast.success("You're subscribed. Welcome aboard.");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    } finally { setLoading(false); }
  };

  const inline = variant === "inline";
  // In-body block follows the article H1 directly, so it is an H2 there; the sidebar card stays H3.
  const Heading = inline ? "h2" : "h3";
  return (
    <aside className={`relative bg-[#0d1117] text-white ${inline ? "my-10 md:my-12 p-6 md:p-8" : "p-5"}`} data-testid={`subscribe-${variant}`}>
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="absolute top-3 right-3 p-1 text-white/40 hover:text-white transition-colors" data-testid={`subscribe-${variant}-dismiss`}><X className="w-4 h-4" /></button>
      <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#B89947] mb-3"><Mail className="w-3.5 h-3.5" /> Newsletter</span>
      <Heading className={`font-serif font-bold leading-tight mb-2 ${inline ? "text-2xl md:text-3xl" : "text-lg"}`}>
        {inline ? "Enjoying this story? Get the next one first." : "Stories worth your inbox."}
      </Heading>
      <p className={`text-neutral-400 leading-relaxed ${inline ? "text-sm md:text-base mb-6 max-w-lg" : "text-xs mb-4"}`}>
        {inline ? "The people, companies and ideas shaping India, in a short editorial brief. No noise." : "One short editorial brief. No noise."}
      </p>
      {done ? (
        <div className="flex items-center gap-2 text-green-400 text-sm font-medium" data-testid={`subscribe-${variant}-success`}><CheckCircle className="w-4 h-4" /> Thank you for subscribing.</div>
      ) : (
        <form onSubmit={submit} className={`flex ${inline ? "flex-col sm:flex-row" : "flex-col"} gap-3`}>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="flex-1 bg-white/5 border border-white/15 focus:border-[#B89947] outline-none px-4 py-3 text-sm text-white placeholder-white/35 transition-colors" data-testid={`subscribe-${variant}-email`} />
          <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#B89947] text-[#0d1117] text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-[#d4b25c] transition-colors disabled:opacity-50" data-testid={`subscribe-${variant}-submit`}>
            {loading ? "…" : <>Subscribe <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </form>
      )}
      {!done && (
        <p className="text-neutral-400 text-[11px] leading-relaxed mt-4" data-testid={`subscribe-${variant}-consent`}>
          By subscribing, you agree to our <Link to="/privacy-policy" className="underline hover:text-neutral-300">Privacy Policy</Link>. Unsubscribe at any time.
        </p>
      )}
    </aside>
  );
};

export default InlineSubscribe;
